import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { IntlProvider, FormattedMessage } from 'react-intl';

import de from '../lang/de.json';
import en from '../lang/en.json';

export const messagesByLocale = {
  en,
  de,
};

export const LANGUAGE_OPTIONS = [
  {
    value: 'EN',
    label: <FormattedMessage defaultMessage="EN" id="zsr0Bz" />,
  },
  {
    value: 'DE',
    label: <FormattedMessage defaultMessage="GE" id="16tJ6v" />,
  },
] as const;

export type LanguageOption = (typeof LANGUAGE_OPTIONS)[number];

export type Language = typeof LANGUAGE_OPTIONS extends readonly (infer U)[]
  ? U extends { readonly value: infer K }
    ? K
    : U
  : typeof LANGUAGE_OPTIONS;

const DEFAULT_LANGUAGE = 'DE';

const Context = createContext<{
  readonly language: Language;
  readonly handleLanguageChange: (lang: Language) => void;
}>({
  language: DEFAULT_LANGUAGE,
  handleLanguageChange: () => {},
});

type locales =
  typeof messagesByLocale extends Record<infer U, unknown> ? U : unknown;

const LOCALES: Record<Language, locales> = {
  EN: 'en',
  DE: 'de',
};

const isValidLanguage = (lang: string | unknown): lang is Language =>
  (lang as Language) in LOCALES;

export const LanguageProvider = ({ children }: React.PropsWithChildren) => {
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const locale = LOCALES[language];
  const messages = messagesByLocale[locale];

  const handleLanguageChange = useCallback((lang: Language) => {
    window.localStorage.setItem('language', lang);
    setLanguage(lang);
  }, []);

  useEffect(() => {
    const browserLang =
      // 1. ?language query param
      new URLSearchParams(window.location.search)
        .get('language')
        ?.toUpperCase() ??
      // 2. Previously stored locale
      window.localStorage.getItem('language');

    if (browserLang !== null && isValidLanguage(browserLang)) {
      window.localStorage.setItem('language', browserLang);
      setLanguage(browserLang);
    }
  }, []);

  return (
    <Context.Provider value={{ language, handleLanguageChange }}>
      <IntlProvider
        defaultLocale="de"
        locale={locale}
        messages={messages}
        onError={(err) => {
          if (err.code === 'MISSING_TRANSLATION') {
            return;
          }
          throw err;
        }}
      >
        {children}
      </IntlProvider>
    </Context.Provider>
  );
};

export const useLanguage = () => useContext(Context);
